import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiClient, extractApiError } from '../api/axios';
import { API } from '../constants/api';
import { DEFAULT_PAGE_SIZE } from '../constants/ui';

const initialState = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  total: null,
  loading: false,
  modal: false,
  error: '',
  success: false,
  deleted: false,
  vendors: [],

  statusPostVendor: 'idle',
  errorPostVendor: null,

  bottles: null,
  loadingBottles: false,
  errorBottles: '',
  successBottles: false,

  totalOrderStats: null,
  loadingTotalOrderStats: false,
  errorTotalOrderStats: '',
  successTotalOrderStats: false,

  availabelPleaces: null,

  update: false,

  statusCreateAvailablePlaces: 'idle',
  errorCreateAvailablePlaces: null,

  statusUpdateAvailablePlaces: 'idle',
  errorUpdateAvailablePlaces: null,
  vendorId: null,

  vendor: null,
  bottlesLoading: false,
  bottlesError: null,
  vendorTab: 'profile',
};

export const fetchVendors = createAsyncThunk(
  'vendors/fetchVendors',
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = {};
      if (params.page) query.page = params.page;
      if (params.pageSize) query.page_size = params.pageSize;
      if (params.search) query.search = params.search;
      const response = await apiClient.get(API.vendors.list, { params: query });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchVendorById = createAsyncThunk('vendors/fetchVendorById', async (id, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API.vendors.byId(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const postVendor = createAsyncThunk('vendors/postVendor', async (data, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.vendors.list, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error?.response?.data?.[0] || extractApiError(error));
  }
});

export const deleteVendor = createAsyncThunk('vendors/deleteVendor', async (id, { rejectWithValue }) => {
  try {
    const response = await apiClient.del(API.vendors.byId(id));
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const fetchBottles = createAsyncThunk(
  'vendors/fetchBottles',
  async ({ id, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.vendors.stats(id), {
        params: { start_date: startDate, end_date: endDate },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchBottlesVendor = createAsyncThunk(
  'vendors/fetchBottlesVendor',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.vendors.statsMe, {
        params: { start_date: startDate, end_date: endDate },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchTotaOrderStats = createAsyncThunk(
  'vendors/fetchTotaOrderStats',
  async ({ id, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.vendors.totalOrderStats(id), {
        params: { start_date: startDate, end_date: endDate },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchTotaOrderStatsVendor = createAsyncThunk(
  'vendors/fetchTotaOrderStatsVendor',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.vendors.totalOrderStatsMe, {
        params: { start_date: startDate, end_date: endDate },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const fetchVendorAvailablePleaces = createAsyncThunk(
  'vendors/fetchVendorAvailablePleaces',
  async ({ vendor_id: id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API.vendors.availablePlaces, {
        params: { vendor_id: id },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const createVendorAvailablePleaces = createAsyncThunk(
  'vendors/createVendorAvailablePleaces',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API.vendors.availablePlaces, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.[0] || extractApiError(error));
    }
  }
);

export const updateVendorAvailablePleaces = createAsyncThunk(
  'vendors/updateVendorAvailablePleaces',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(API.vendors.availablePlaceById(id), data);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const deleteVendorAvailablePleace = createAsyncThunk(
  'vendors/deleteVendorAvailablePleace',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await apiClient.del(API.vendors.availablePlaceById(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const deleteVendorAvailablePleaceDistrict = createAsyncThunk(
  'vendors/deleteVendorAvailablePleaceDistrict',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.del(API.vendors.availablePlaceDistricts(id), data);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const addBottles = createAsyncThunk('vendors/addBottles', async (bottles, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(API.vendors.addBottles, { new_bottles: bottles });
    return response.data;
  } catch (error) {
    return rejectWithValue(extractApiError(error));
  }
});

export const vendorsSlice = createSlice({
  name: 'vendors',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchVendors.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchVendors.fulfilled, (state, action) => {
      state.loading = false;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.vendors = action.payload.results;
      state.success = true;
    });
    builder.addCase(fetchVendors.rejected, (state, action) => {
      state.error = action.payload;
      if (action.payload !== 'Request failed with status code 401') {
        toast.error(action.payload);
      }
      state.loading = false;
      state.success = false;
    });

    builder.addCase(fetchVendorById.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(fetchVendorById.fulfilled, (state, action) => {
      state.loading = false;
      state.vendor = action.payload;
    });
    builder.addCase(fetchVendorById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      toast.error(action.payload);
    });

    builder.addCase(fetchVendorAvailablePleaces.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.success = false;
    });
    builder.addCase(fetchVendorAvailablePleaces.fulfilled, (state, action) => {
      state.loading = false;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.total = action.payload.total;
      state.availabelPleaces = action.payload.results;
      state.success = true;
    });
    builder.addCase(fetchVendorAvailablePleaces.rejected, (state, action) => {
      state.error = action.payload;
      toast.error(action.payload);
      state.loading = false;
      state.success = false;
    });

    builder.addCase(createVendorAvailablePleaces.pending, (state) => {
      // Use the dedicated create-status flag, NOT the shared `state.loading`
      // (the vendors table reads that and it was never reset → stuck spinner).
      state.statusCreateAvailablePlaces = 'loading';
      state.errorCreateAvailablePlaces = null;
    });
    builder.addCase(createVendorAvailablePleaces.fulfilled, (state) => {
      state.statusCreateAvailablePlaces = 'success';
    });
    builder.addCase(createVendorAvailablePleaces.rejected, (state, action) => {
      state.statusCreateAvailablePlaces = 'fail';
      state.errorCreateAvailablePlaces = action.payload;
      toast.error(action.payload);
    });

    builder.addCase(updateVendorAvailablePleaces.pending, (state) => {
      // Use the dedicated update-status flag, NOT the shared `state.loading`
      // (never reset on fulfilled/rejected → stuck vendors-table spinner).
      state.statusUpdateAvailablePlaces = 'loading';
      state.errorUpdateAvailablePlaces = null;
    });
    builder.addCase(updateVendorAvailablePleaces.fulfilled, (state) => {
      state.statusUpdateAvailablePlaces = 'success';
    });
    builder.addCase(updateVendorAvailablePleaces.rejected, (state, action) => {
      state.statusUpdateAvailablePlaces = 'fail';
      state.errorUpdateAvailablePlaces = action.payload;
      toast.error(action.payload);
    });

    builder.addCase(fetchBottles.pending, (state) => {
      state.loadingBottles = true;
      state.errorBottles = '';
      state.successBottles = false;
    });
    builder.addCase(fetchBottles.fulfilled, (state, action) => {
      state.loadingBottles = false;
      state.bottles = action.payload;
      state.successBottles = true;
    });
    builder.addCase(fetchBottles.rejected, (state, action) => {
      state.errorBottles = action.payload;
      toast.error(action.payload);
      state.loadingBottles = false;
      state.successBottles = false;
    });

    builder.addCase(fetchBottlesVendor.pending, (state) => {
      state.loadingBottles = true;
      state.errorBottles = '';
      state.successBottles = false;
    });
    builder.addCase(fetchBottlesVendor.fulfilled, (state, action) => {
      state.loadingBottles = false;
      state.bottles = action.payload;
      state.successBottles = true;
    });
    builder.addCase(fetchBottlesVendor.rejected, (state, action) => {
      state.errorBottles = action.payload;
      toast.error(action.payload);
      state.loadingBottles = false;
      state.successBottles = false;
    });

    builder.addCase(fetchTotaOrderStats.pending, (state) => {
      state.loadingTotalOrderStats = true;
      state.errorTotalOrderStats = '';
      state.successTotalOrderStats = false;
    });
    builder.addCase(fetchTotaOrderStats.fulfilled, (state, action) => {
      state.loadingTotalOrderStats = false;
      state.totalOrderStats = action.payload;
      state.successTotalOrderStats = true;
    });
    builder.addCase(fetchTotaOrderStats.rejected, (state, action) => {
      state.errorTotalOrderStats = action.payload;
      toast.error(action.payload);
      state.loadingTotalOrderStats = false;
      state.successTotalOrderStats = false;
    });

    builder.addCase(fetchTotaOrderStatsVendor.pending, (state) => {
      state.loadingTotalOrderStats = true;
      state.errorTotalOrderStats = '';
      state.successTotalOrderStats = false;
    });
    builder.addCase(fetchTotaOrderStatsVendor.fulfilled, (state, action) => {
      state.loadingTotalOrderStats = false;
      state.totalOrderStats = action.payload;
      state.successTotalOrderStats = true;
    });
    builder.addCase(fetchTotaOrderStatsVendor.rejected, (state, action) => {
      state.errorTotalOrderStats = action.payload;
      toast.error(action.payload);
      state.loadingTotalOrderStats = false;
      state.successTotalOrderStats = false;
    });

    builder.addCase(postVendor.pending, (state) => {
      state.statusPostVendor = 'loading';
    });
    builder.addCase(postVendor.fulfilled, (state, action) => {
      state.statusPostVendor = 'success';
      state.vendor = action.payload;
    });
    builder.addCase(postVendor.rejected, (state, action) => {
      state.statusPostVendor = 'fail';
      state.errorPostVendor = action.payload;
    });

    builder.addCase(addBottles.pending, (state) => {
      state.bottlesLoading = true;
    });
    builder.addCase(addBottles.fulfilled, (state) => {
      state.bottlesLoading = false;
      toast.success("Idishlar muvaffaqiyatli qo'shildi");
    });
    builder.addCase(addBottles.rejected, (state, action) => {
      state.bottlesLoading = false;
      state.bottlesError = action.payload;
      toast.error(action.payload);
    });

    builder.addCase(deleteVendor.fulfilled, (state) => {
      state.deleted = true;
    });
    builder.addCase(deleteVendor.rejected, (state, action) => {
      toast.error(action.payload);
    });
  },

  reducers: {
    resetStatus: (state) => {
      state.statusPostVendor = 'idle';
      state.errorPostVendor = '';
    },
    setVendorTab: (state, action) => {
      state.vendorTab = action.payload;
    },
    setUpdate: (state) => {
      state.update = !state.update;
    },
    setModal: (state, action) => {
      state.modal = action.payload;
    },
    setVendorId: (state, { payload }) => {
      state.vendorId = payload;
    },
    resetDeleted: (state) => {
      state.deleted = false;
    },
  },
});

export const { setVendorTab, setModal, setUpdate, resetStatus, setVendorId, resetDeleted } = vendorsSlice.actions;

export default vendorsSlice.reducer;
